import type { FilterHistoryEntry, UrlSerializableFilterState } from '@/types/nmed/nmed-filter-types';
import { computed, ref } from 'vue';
import { areFiltersEqual } from '@/utils/filter-url-utils';

/**
 * Composable for managing filter undo history.
 * Maintains an in-memory stack of filter states for undo functionality.
 *
 * @param maxHistorySize Maximum number of history entries to keep (default: 50)
 */
export function useNmedDocSearchUndoHistory(maxHistorySize: number = 50) {
   const history = ref<FilterHistoryEntry[]>([]);
   const currentIndex = ref<number>(-1);

   /**
    * Whether undo is available (there's previous history)
    */
   const canUndo = computed(() => currentIndex.value > 0);

   /**
    * Number of undo steps available
    */
   const undoStepsAvailable = computed(() => currentIndex.value);

   /**
    * Deep clones state using JSON serialization to avoid Vue reactive proxy issues
    */
   function deepClone(state: UrlSerializableFilterState): UrlSerializableFilterState {
      return JSON.parse(JSON.stringify(state));
   }

   /**
    * Pushes a new state to the history stack.
    * If we're not at the end of history, truncates forward history.
    * Skips if new state equals current state.
    */
   function pushState(state: UrlSerializableFilterState): void {
      // Don't push if it's the same as current state
      if (currentIndex.value >= 0 && currentIndex.value < history.value.length) {
         const currentState = history.value[currentIndex.value].state;
         if (areFiltersEqual(currentState, state)) {
            return;
         }
      }

      // Truncate forward history if we're not at the end
      if (currentIndex.value < history.value.length - 1) {
         history.value = history.value.slice(0, currentIndex.value + 1);
      }

      // Add new entry - use JSON clone to avoid reactive proxy issues
      history.value.push({
         timestamp: Date.now(),
         state: deepClone(state),
      });

      // Trim oldest entries if over max size
      if (history.value.length > maxHistorySize) {
         history.value = history.value.slice(-maxHistorySize);
      }

      // Update index to point to newest entry
      currentIndex.value = history.value.length - 1;
   }

   /**
    * Returns the previous state and moves index back.
    * Returns null if cannot undo.
    */
   function undo(): UrlSerializableFilterState | null {
      if (!canUndo.value) {
         return null;
      }

      currentIndex.value--;
      return deepClone(history.value[currentIndex.value].state);
   }

   /**
    * Clears all history
    */
   function clearHistory(): void {
      history.value = [];
      currentIndex.value = -1;
   }

   /**
    * Gets the current state in history (if any)
    */
   function getCurrentState(): UrlSerializableFilterState | null {
      if (currentIndex.value >= 0 && currentIndex.value < history.value.length) {
         return deepClone(history.value[currentIndex.value].state);
      }
      return null;
   }

   return {
      canUndo,
      undoStepsAvailable,
      pushState,
      undo,
      clearHistory,
      getCurrentState,
   };
}
